import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs';
import { NotificationVet, NotificationVetNotificationType } from 'src/app/models/notifications/notification-vet.model';
import { NotificationsService } from 'src/app/services/notifications.service';
import { BaseUnsubscribeComponent } from 'src/app/shared-new/components/base-unsubscribe/base-unsubscribe.component';
import { ToastService } from 'src/app/shared-new/services/toast.service';
import { NotificationTypePopupComponent } from '../notification-type-popup/notification-type-popup.component';
import Swal from 'sweetalert2';
import { CommonService } from '../../services/common.service';
import { CountryISO, PhoneNumberFormat, SearchCountryField, NgxIntlTelInputComponent } from 'ngx-intl-tel-input';
import Constants from 'src/app/constants.json';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'vet-user-notification-setting',
  templateUrl: './vet-user-notification-setting.component.html',
  styleUrls: ['./vet-user-notification-setting.component.scss'],
})
export class VetUserNotificationSettingComponent extends BaseUnsubscribeComponent implements OnInit, AfterViewInit {
  @Input() public data: any;
  @Input() public isUseBuiltInSave = true;
  /**
   * A SALES COMPANY looking at a vet's contacts (sale Vets list / vet profile popup). D-S3 revised (2026-10-06):
   * it may see them and ADD new ones, but change or delete only contacts its own users added. Every other contact
   * is shown read-only and posted back unchanged (the API saves the full set and refuses a changed one with 403).
   */
  @Input() public salesCompanyView = false;
  public contactsUnavailableMessage: string | null = null;
  /** The contacts exactly as the API returned them, by id (posted back verbatim when read-only). */
  private originalContacts = new Map<number, any>();
  /** Contacts the caller's company added (may be changed/deleted in the sales company view). */
  private ownContactIds = new Set<number>();

  public static readonly CONTACT_NOT_YOURS_MESSAGE =
    'You can only change or delete contacts your company added. Contacts the vet set up can only be changed by the vet or their clinic admin.';
  public static readonly CONTACTS_NOT_AVAILABLE_MESSAGE =
    "This vet's notification contacts are not available to your company. They become visible once the vet has access to, or has submitted to, one of your sales.";
  public notificationFormArrayForm: FormGroup;
  public emailForms: FormGroup[] = [];
  public mobileForms: FormGroup[] = [];
  public hasEmailNotifications: boolean = false;
  public hasMobileNotifications: boolean = false;
  public isLoading: boolean = false;
  public isSubmitLoading: boolean = false;
  public submitted = false;
  public countries: any[] = [];
  private _notificationTypes: NotificationVetNotificationType[] = [];
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = Constants.preferredCountries.map((item) => item as CountryISO);

  constructor(
   
    private readonly fb: FormBuilder,
    public readonly notificationsService: NotificationsService,
    private readonly toastService: ToastService,
    private modalService: NgbModal,
    private readonly router: Router,
    private readonly commonService: CommonService,
    private readonly authService: AuthService
  ) {
    super();
    this.notificationFormArrayForm = this.fb.group({
      mobileForms: this.mobileForms,
      emailForms: this.emailForms
    });
  }

  private static buildFormArray(models: NotificationVet[]): FormGroup[] {
    return models.map(model => NotificationVet.asFormGroup(model));
  }

  adjustTelInputDropdown(ele: HTMLElement) {
    const dropdown = ele.closest('.iti--allow-dropdown').querySelector('.iti__dropdown-content') as HTMLElement;
    if (!dropdown) return;

    // Reset to default downward style first
    const rect = dropdown.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.top;
    const spaceAbove = rect.top;

    // Reset
    dropdown.style.inset = '';
    dropdown.style.top = '';
    dropdown.style.bottom = '';
    dropdown.style.transform = '';
    dropdown.style.zIndex = '';
    dropdown.style.maxHeight = '';
    dropdown.style.overflowY = '';

    // If no space below and more space above → open upwards
    if (spaceBelow < dropdown.offsetHeight + 44 && spaceAbove > spaceBelow) {
      dropdown.style.inset = 'auto auto 100% 0px';
      dropdown.style.top = 'auto';
      dropdown.style.bottom = '100%';
      dropdown.style.transform = 'none';
      dropdown.style.maxHeight = '300px';
      dropdown.style.overflowY = 'auto';
    }
  }

  ngAfterViewInit() {
    document.addEventListener('click', (e) => {

      // When clicking the flag (opens the dropdown)
      if ((e.target as HTMLElement).closest('.iti__selected-flag')) {
        setTimeout(() => this.adjustTelInputDropdown((e.target as HTMLElement).closest('.iti__selected-flag')), 50);
      }
    });
  }


  
  areFormGroupsValid(formGroups: FormGroup[]): boolean {
    var count = 0;
    return formGroups.every(formGroup => {
      count = count + 1;
      return Object.keys(formGroup.controls).every(controlName => {
        const control = formGroup.get(controlName);
        control?.markAsTouched(); // optional: show validation feedback
        return control?.valid;
      });
    });
  }

  isFormValid(): boolean {
    // Read-only rows are posted back exactly as loaded, so they are not re-validated here.
    const isEmailValid = this.areFormGroupsValid(this.emailForms.filter(fg => !this.isLocked(fg)));
    const isMobileValid = this.areFormGroupsValid(this.mobileForms.filter(fg => !this.isLocked(fg)));
    return isEmailValid && isMobileValid;
  }

  /** Read-only for this viewer: a sales company looking at a contact it did not add. */
  public isLocked(formGroup: FormGroup): boolean {
    const id = +(formGroup?.get('userNotificationContactId')?.value || 0);
    return this.salesCompanyView && id > 0 && !this.ownContactIds.has(id);
  }

  /** Sales company view: a contact its company added (or is adding now). */
  public isOwnContact(formGroup: FormGroup): boolean {
    const id = +(formGroup?.get('userNotificationContactId')?.value || 0);
    return this.salesCompanyView && (id <= 0 || this.ownContactIds.has(id));
  }

  /**
   * Whether the CALLER may change/delete this contact. Uses the API's per-contact `canEdit` when present;
   * otherwise `createdByUserId` = the signed-in user; otherwise unknown, treated as NOT editable (safe default:
   * every prod contact today was created by the vet). See the phase-1 report for the field the API should add.
   */
  public static isEditableByCaller(item: any, currentUserId: number | null | undefined): boolean {
    if (typeof item?.canEdit === 'boolean') {
      return item.canEdit;
    }
    if (item?.createdByUserId != null && currentUserId) {
      return +item.createdByUserId === +currentUserId;
    }
    return false;
  }

  /** A contact the caller may not change, posted back exactly as the API returned it. */
  private static unchangedContact(original: any): any {
    return {
      userNotificationContactId: original.userNotificationContactId,
      email: original.email ?? null,
      mobile: original.mobile ?? null,
      countryCode: original.countryCode ?? null,
      isPrimary: !!original.isPrimary,
      userNotificationType: [...(original.userNotificationType || [])],
    };
  }

  /** Sales company view: swap every read-only contact for its untouched original. */
  private keepLockedContactsUnchanged(items: any[]): any[] {
    if (!this.salesCompanyView) {
      return items;
    }
    return items.map((item) => {
      const id = +(item?.userNotificationContactId || 0);
      const original = id > 0 && !this.ownContactIds.has(id) ? this.originalContacts.get(id) : null;
      return original ? VetUserNotificationSettingComponent.unchangedContact(original) : item;
    });
  }

  /** A clear message for a refused contacts read/save/delete. */
  public static contactErrorMessage(error: any): string {
    const serverMessages = Array.isArray(error?.error)
      ? error.error.map((m: any) => m?.message).filter((m: any) => !!m).join(' ')
      : (typeof error?.error === 'string' ? error.error : '');
    if (error?.status === 403) {
      return serverMessages && !/contacts your company added/i.test(serverMessages)
        ? serverMessages
        : VetUserNotificationSettingComponent.CONTACT_NOT_YOURS_MESSAGE;
    }
    if (error?.status === 404) {
      return VetUserNotificationSettingComponent.CONTACTS_NOT_AVAILABLE_MESSAGE;
    }
    return serverMessages || 'The notification contacts could not be saved. Please try again.';
  }
  getFormGroupsValues(formGroups: FormGroup[]): any[] {
    return formGroups.map(formGroup => formGroup.value);
  }
    private setDefaultCountryFromDialCode(code: string): string {
    var countryCode = '';
    if (code) {
      const dialCode = code.replace('+', '');
      const matched = (window as any).intlTelInputGlobals.getCountryData().find((c: any) => c.dialCode === dialCode);

      if (matched) {
        countryCode = matched.iso2.toUpperCase() as CountryISO;
      } 
    }
    return countryCode;
  }
  // Simple country code detection - expand as needed
    private getCountryCodeFromPhone(phoneNumber: string): string {
      const matchedCountry = typeof phoneNumber === 'undefined' || phoneNumber === '' ? '' : this.countries.find(country =>
        phoneNumber.startsWith(country.code));

    return matchedCountry?.code ?? ''; // Default
    }
  public submit(): void {
    this.submitted = true;
    if (this.isFormValid()) {
      const notificationEmailVets: NotificationVet[] = this.getFormGroupsValues(this.emailForms)
      const notificationMobileVets: NotificationVet[] = this.getFormGroupsValues(this.mobileForms)
      // Merge the two arrays
      let mergedNotificationVets: NotificationVet[] = [
        ...notificationEmailVets,
        ...notificationMobileVets
      ];
      mergedNotificationVets = mergedNotificationVets.map((item : any) => {
        if (item.email) {
          item.isPrimary = item.isPrimaryEmail;
        } 
        if (item.mobile) {
          item.isPrimary = item.isPrimaryMobile;
          item.mobile = item.mobileNumberControl?.internationalNumber;
          item.countryCode= this.getCountryCodeFromPhone(item.mobileNumberControl?.internationalNumber);
          item.mobile = item.mobile.replace(item.countryCode, '').trim();
        } 
        item.userNotificationType = item.userNotificationType.map((n: any) => {
          return n.notificationTypeId
        });


        delete item.isPrimaryEmail;
        delete item.isPrimaryMobile;
        return item;
      })
     
      mergedNotificationVets = this.keepLockedContactsUnchanged(mergedNotificationVets);
      this.isSubmitLoading = true;
      this.notificationsService.updateVetNotifications(mergedNotificationVets).subscribe({
        next: () => {
          this.toastService.success('Notifications updated successfully');
          this.isSubmitLoading = false;
          this.router.navigate(['/sales'])
        },
        error: (error) => {
          this.isSubmitLoading = false;
          this.toastService.error(VetUserNotificationSettingComponent.contactErrorMessage(error), 'Not saved');
        }
      });
    }
  }
  private isNullOrUndefined(value: any): value is null | undefined | '' {
    return (value === '' || value === undefined) ? null : value;
  }
  getFormData() {
    this.submitted = true;
    if (this.isFormValid()) {
      const notificationEmailVets: NotificationVet[] = this.getFormGroupsValues(this.emailForms)
      const notificationMobileVets: NotificationVet[] = this.getFormGroupsValues(this.mobileForms)
      // Merge the two arrays
      let mergedNotificationVets: NotificationVet[] = [
        ...notificationEmailVets,
        ...notificationMobileVets
      ];
      mergedNotificationVets = mergedNotificationVets.map((item: any) => {
        if (item.email) {
          item.isPrimary = item.isPrimaryEmail;
        }        
        if (this.isNullOrUndefined(item.mobileNumberControl) != null && item.mobileNumberControl?.number) {
          item.isPrimary = item.isPrimaryMobile;
          //item.mobileNumberControl = item.mobileNumberControl?.internationalNumber || item.mobileNumberControl;
          item.mobile = item.mobileNumberControl?.internationalNumber;
          item.countryCode = this.getCountryCodeFromPhone(item.mobileNumberControl?.internationalNumber);
          item.mobile = item.mobile.replace(item.countryCode, '').trim();
        }
        item.userNotificationType = item.userNotificationType.map((n: any) => {
          return n.notificationTypeId;
        });
        delete item.isPrimaryEmail;
        delete item.isPrimaryMobile;
        return item;
      })

      if (this.contactsUnavailableMessage) {
        return null; // nothing was readable, so nothing is posted
      }
      this.isSubmitLoading = true;
      return this.keepLockedContactsUnchanged(mergedNotificationVets);
    }
    return null;
  }
  cancel() {
    this.router.navigate(['/sales'])
  }
  onPrimaryEmailChange(selectedIndex: number) {
    if (this.salesCompanyView) {
      return; // a sales company does not choose the vet's primary contact
    }
    this.emailForms.forEach((formGroup, index) => {
      if (index === selectedIndex) {
        formGroup.get('isPrimaryEmail').setValue(true);
      } else {
        formGroup.get('isPrimaryEmail').setValue(false);
      }
    });
  }

  onPrimaryMobileChange(selectedIndex: number) {
    if (this.salesCompanyView) {
      return; // a sales company does not choose the vet's primary contact
    }
    this.mobileForms.forEach((formGroup, index) => {
      if (index === selectedIndex) {
        formGroup.get('isPrimaryMobile').setValue(true);
      } else {
        formGroup.get('isPrimaryMobile').setValue(false);
      }
    });
  }
  public initiateForm(): void {
    this.isLoading = true;
    this.contactsUnavailableMessage = null;
    this.notificationsService.getVetNotifications(this.data?.userId).subscribe({ next: response => {
      this.isLoading = false;
      const currentUserId = this.authService.getUserTokenResponse()?.userId;
      this.originalContacts.clear();
      this.ownContactIds.clear();
      (response || []).forEach((item: any) => {
        const id = +(item?.userNotificationContactId || 0);
        if (id > 0) {
          this.originalContacts.set(id, item);
          if (VetUserNotificationSettingComponent.isEditableByCaller(item, currentUserId)) {
            this.ownContactIds.add(id);
          }
        }
      });

      const notificationVets = (response || []).map((item: any) => {
        
        const vetNotification = {
          userNotificationContactId: item.userNotificationContactId,
          email: item.email,
          mobile: item.mobile,
          countryCode: item.countryCode,
          isPrimary: item.isPrimary,
          selectedCountryISO: item?.mobile != null ? this.setDefaultCountryFromDialCode(item?.countryCode ).trim(): null,
          //mobileNumberControl: item?.mobile != null ?item?.mobile.replace(item?.countryCode, '').trim(): null,
          mobileNumberControl:  {
            number: item?.mobile,
            internationalNumber: item?.countryCode+ '' + item?.mobile,
            nationalNumber: '',
            countryCode: this.setDefaultCountryFromDialCode(item?.countryCode ).trim(),
            dialCode: item?.countryCode
            },
          userNotificationType: item.userNotificationType.map((type: any) => {
            const notificationType = {
              notificationTypeId: type
            } as NotificationVetNotificationType;
            return notificationType;
          })
        } as NotificationVet;
        return vetNotification;
      });
      const formArray = this.fb.array(VetUserNotificationSettingComponent.buildFormArray(notificationVets));
      this.notificationFormArrayForm.setControl('notifications', formArray);
      this.splitForms();
    }, error: (error) => {
      this.isLoading = false;
      this.emailForms = [];
      this.mobileForms = [];
      this.contactsUnavailableMessage = error?.status === 404 || error?.status === 403
        ? VetUserNotificationSettingComponent.CONTACTS_NOT_AVAILABLE_MESSAGE
        : 'The notification contacts could not be loaded. Please try again.';
    } });
  }

  private splitForms(): void {
    const controls = this.notificationFormArrayForm.get('notifications') as FormArray;
    this.emailForms = controls.controls.filter(formGroup => formGroup.get('email').value !== null) as FormGroup[];
    this.mobileForms = controls.controls.filter(formGroup => formGroup.get('mobile').value !== null) as FormGroup[];
    this.hasEmailNotifications = this.emailForms.length > 0;
    this.hasMobileNotifications = this.mobileForms.length > 0;
  }

  addEmail(): void {
    const emailGroup = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      isPrimaryEmail: [false],
      userNotificationType: this.fb.array(this._notificationTypes),
    });
    this.emailForms.push(emailGroup);
  }

  addMobile(): void {
    const mobileGroup = this.fb.group({
      countryCode: ['+61'],
      mobile: [''],
      mobileNumberControl: [null, Validators.required],
      isPrimaryMobile: [false],
      userNotificationType: this.fb.array(this._notificationTypes),
    });
    this.mobileForms.push(mobileGroup);
  }  

  public openNotificationTypeModal(formGroup: FormGroup): void {
    this.notificationsService.getNotificationTypes().pipe(takeUntil(this.unsubscribe$)).subscribe((response: NotificationVetNotificationType[]) => {

      const notificationTypes = formGroup.get('userNotificationType')?.value as NotificationVetNotificationType[];
      const modalRef = this.modalService.open(NotificationTypePopupComponent, {
        size: '50-width',
        centered: true,
        keyboard: false,
        backdrop: 'static',
      });

      // Pass all available notification types and the currently selected types to the modal
      modalRef.componentInstance.notificationTypes = response;
      modalRef.componentInstance.selectedTypes = notificationTypes;
      modalRef.componentInstance.title = (formGroup.get('email') && formGroup.get('email').value) ? formGroup.get('email').value : `${formGroup.get('countryCode').value} ${formGroup.get('mobile').value}`;

      modalRef.result.then((updatedTypes: NotificationVetNotificationType[]) => {
        if (updatedTypes) {
          const formArray = formGroup.get('userNotificationType') as FormArray;
          formArray.clear();
          updatedTypes.forEach(type => formArray.push(NotificationVetNotificationType.asFormGroup(type)));
        }
      }).catch(() => { });
    });
  }


  ngOnInit(): void {
    this.notificationsService.getNotificationTypes().pipe(takeUntil(this.unsubscribe$)).subscribe((response: NotificationVetNotificationType[]) => {
      this._notificationTypes = response;
      this.initiateForm();
    });
    this.commonService.getCountries().subscribe((response) => {
      if (response.body) {
        const responseData = JSON.parse(JSON.stringify(response.body));
        if (responseData) {
          this.countries = responseData;

          if (this.countries && this.countries?.length > 0) {

          }
        }
      }
    });
  }

  removeNotificationContact(formGroup: FormGroup, isEmail: boolean): void {
    if (this.isLocked(formGroup)) {
      this.toastService.error(VetUserNotificationSettingComponent.CONTACT_NOT_YOURS_MESSAGE, 'Not deleted');
      return;
    }
    const userNotificationContactId = formGroup.get('userNotificationContactId')?.value;

    if (userNotificationContactId > 0) {
      // Call the API to delete the notification contact
      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Delete',
        confirmButtonColor: '#1b6da6',
      }).then((result) => {
        if (result.value) {
          this.isLoading = true;
          this.notificationsService.deleteNotificationContact(userNotificationContactId)
            .subscribe({
              next: () => {
                this.isLoading = false;
                this.originalContacts.delete(+userNotificationContactId);
                this.ownContactIds.delete(+userNotificationContactId);
                if (isEmail) {
                  // Handle successful deletion
                  this.removeFormGroupEmail(formGroup);
                } else {
                  this.removeFormGroupMobile(formGroup);
                }
              },
              error: (error) => {
                this.isLoading = false;
                if (error?.status === 403) {
                  // The API says this contact is not ours: show it read-only from now on.
                  this.ownContactIds.delete(+userNotificationContactId);
                }
                this.toastService.error(VetUserNotificationSettingComponent.contactErrorMessage(error), 'Not deleted');
              }
            });
        }
      });
    } else {
      if (isEmail) {
        // Delete locally
        this.removeFormGroupEmail(formGroup);
      } else {
        this.removeFormGroupMobile(formGroup);
      }
    }
  }

  private removeFormGroupEmail(formGroup: FormGroup): void {
    const index = this.emailForms.indexOf(formGroup);
    if (index > -1) {
      this.emailForms.splice(index, 1);
    }
  }
  private removeFormGroupMobile(formGroup: FormGroup): void {
    const index = this.mobileForms.indexOf(formGroup);
    if (index > -1) {
      this.mobileForms.splice(index, 1);
    }
  }
}
